import { INGREDIENTS } from './ingredients'
import { INGREDIENT_CELLS } from './round'

/**
 * Draws the game on a canvas fixed to the viewport between the page content
 * and the header/footer bars: the snake and the word as squares with a 1px
 * gap, the ingredients as pixel art over 2×2 cells.
 *
 * The word is always white, and so is the snake — unless an effect colours
 * it: sick green after the pineapple, and after a chili red → orange → red
 * waves that run down the body from the head (with reduced motion, a single
 * still run over the whole body).
 *
 * An ingredient's art pixels are whole device pixels, as large as fit into
 * its 2×2 cells, and centred there. Each sprite is painted once per size onto
 * a canvas of its own, which every frame then only copies.
 */
const COLOR = '#fff'
const SICK_COLOR = '#b5d334'
const HOT_RED = [255, 45, 25]
const HOT_ORANGE = [255, 159, 28]
const HOT_SHADES = 12
// One red → orange → red wave covers this many cells and moves on by one
// wave length in this much game time.
const HOT_WAVE_CELLS = 10
const HOT_WAVE_MS = 600
// A fading ingredient (the pineapple's last seconds with reduced motion).
const DIM_ALPHA = 0.4
// Covers every real screen (WQHD+ phones reach 3.5–4). Only a heavily zoomed
// page goes above it; the canvas is then scaled up with `image-rendering:
// pixelated` (40_game.sass), so the art stays sharp, if a little uneven.
const MAX_PIXEL_RATIO = 4
const ART_SIZE = 10

/** Red to orange in HOT_SHADES steps. */
const HOT_COLORS = Array.from({ length: HOT_SHADES }, (_, shade) => {
  const mix = shade / (HOT_SHADES - 1)
  const channels = HOT_RED.map((red, i) => Math.round(red + (HOT_ORANGE[i] - red) * mix))
  return `rgb(${channels.join(' ')})`
})

/**
 * Where the index-th of `length` cells from the head lies in its red → orange
 * → red run, from 0 to 1.
 *
 * Moving, a run covers HOT_WAVE_CELLS cells and moves on with game time.
 * Still (reduced motion), one run covers the whole body: red head, orange
 * middle, red tail. A short run fixed to the body would not stand still on
 * the screen — every spot the snake crosses would flicker between red and
 * orange, the faster the snake the quicker (up to 4× a second). Stretched
 * over the whole body, a spot turns orange and back to red once while the
 * snake passes over it.
 */
function hotPhase(index, length, time, still) {
  if (still) return length > 1 ? index / (length - 1) : 0
  return (((index / HOT_WAVE_CELLS - time / HOT_WAVE_MS) % 1) + 1) % 1
}

function hotColor(phase) {
  // 0 → red, ½ → orange, 1 → red again.
  const mix = 1 - Math.abs(phase * 2 - 1)
  return HOT_COLORS[Math.round(mix * (HOT_SHADES - 1))]
}

/** One sprite at `scale` device pixels per art pixel. */
function paintSprite(art, scale) {
  const canvas = document.createElement('canvas')
  canvas.width = ART_SIZE * scale
  canvas.height = ART_SIZE * scale
  const context = canvas.getContext('2d')
  if (!context) return null

  art.rows.forEach((row, y) => {
    Array.from(row).forEach((key, x) => {
      const color = art.palette[key]
      if (!color) return
      context.fillStyle = color
      context.fillRect(x * scale, y * scale, scale, scale)
    })
  })
  return canvas
}

export function createRenderer(parent) {
  const canvas = document.createElement('canvas')
  canvas.className = 'snake-canvas'
  canvas.setAttribute('aria-hidden', 'true')
  parent.prepend(canvas)

  const context = canvas.getContext('2d')
  let ratio = 1

  // name → painted sprite, all at spriteScale; a new cell size starts afresh.
  const sprites = new Map()
  let spriteScale = 0

  function spriteFor(name, scale) {
    if (scale !== spriteScale) {
      sprites.clear()
      spriteScale = scale
    }
    if (!sprites.has(name)) {
      const art = INGREDIENTS[name]
      sprites.set(name, art ? paintSprite(art, scale) : null)
    }
    return sprites.get(name)
  }

  return {
    canvas,

    /** Match the backing store to the CSS size and the screen's pixel ratio. */
    resize(width, height) {
      ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
      const pixelWidth = Math.round(width * ratio)
      const pixelHeight = Math.round(height * ratio)
      if (canvas.width !== pixelWidth) canvas.width = pixelWidth
      if (canvas.height !== pixelHeight) canvas.height = pixelHeight
    },

    /**
     * @param {object|null} grid  see layout.js
     * @param {object} [items]
     * @param {Array<Iterable<{x: number, y: number}>>} [items.squares]  cells drawn as white squares
     * @param {{cells: Array<{x: number, y: number}>, look?: 'hot'|'sick'|null, time?: number, still?: boolean}|null} [items.snake]
     *   the snake, head first; `time` (game time, ms) moves the hot waves, `still`
     *   (reduced motion) fixes one run over the whole body instead
     * @param {Iterable<{x: number, y: number, name: string, dim?: boolean}>} [items.ingredients]  top-left cells
     */
    draw(grid, { squares = [], snake = null, ingredients = [] } = {}) {
      if (!context) return
      context.clearRect(0, 0, canvas.width, canvas.height)
      if (!grid) return

      // Snap every edge to device pixels, so squares, gaps and art pixels stay crisp.
      const px = (value) => Math.round(value * ratio)
      const gap = Math.max(1, Math.round(ratio))
      const square = (x, y) => {
        const left = px(grid.offsetX + x * grid.cell)
        const top = px(grid.offsetY + y * grid.cell)
        const right = px(grid.offsetX + (x + 1) * grid.cell)
        const bottom = px(grid.offsetY + (y + 1) * grid.cell)
        context.fillRect(left, top, right - left - gap, bottom - top - gap)
      }

      context.fillStyle = COLOR
      for (const cells of squares) {
        for (const { x, y } of cells) square(x, y)
      }

      if (snake) {
        const { cells, look = null, time = 0, still = false } = snake
        context.fillStyle = look === 'sick' ? SICK_COLOR : COLOR
        cells.forEach(({ x, y }, index) => {
          if (look === 'hot') {
            context.fillStyle = hotColor(hotPhase(index, cells.length, time, still))
          }
          square(x, y)
        })
      }

      const scale = Math.max(1, Math.floor((INGREDIENT_CELLS * grid.cell * ratio) / ART_SIZE))
      // Resizing the canvas resets this, so it is set on every frame.
      context.imageSmoothingEnabled = false

      for (const { x, y, name, dim = false } of ingredients) {
        const sprite = spriteFor(name, scale)
        if (!sprite) continue
        const left = px(grid.offsetX + x * grid.cell)
        const top = px(grid.offsetY + y * grid.cell)
        const width = px(grid.offsetX + (x + INGREDIENT_CELLS) * grid.cell) - left
        const height = px(grid.offsetY + (y + INGREDIENT_CELLS) * grid.cell) - top
        context.globalAlpha = dim ? DIM_ALPHA : 1
        context.drawImage(
          sprite,
          left + Math.floor((width - sprite.width) / 2),
          top + Math.floor((height - sprite.height) / 2)
        )
      }
      context.globalAlpha = 1
    },

    destroy() {
      sprites.clear()
      canvas.remove()
    },
  }
}
